import { useContext, useMemo, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { OrganisationTypeConstants } from "../../constants";
import { ProjectContext } from "../../route/shared/projects/project/ProjectContext";
import { useAuth } from "../../useAuth";
import { getAccessDeniedRoute } from "../../utils/routes";

interface useRouteGuardsReturnData {
  RequireAuth: ({ children }: { children: JSX.Element }) => JSX.Element;
  RequireDeveloperAuth: ({ children }: { children: JSX.Element }) => JSX.Element;
  RequireVerifierAuth: ({ children }: { children: JSX.Element }) => JSX.Element;
  RequireDevelopmentManagerAuth: ({ children }: { children: JSX.Element }) => JSX.Element;
  RequireAssetManagerAuth: ({ children }: { children: JSX.Element }) => JSX.Element;
  RequireUserPermission: ({
    children,
    userPermission,
    redirectRoute,
  }: {
    children: JSX.Element;
    userPermission: string;
    redirectRoute?: string | undefined;
  }) => JSX.Element;
  RequireProjectPermission: ({
    children,
    redirectRoute,
  }: {
    children: JSX.Element;
    redirectRoute?: string | undefined;
  }) => JSX.Element;
}

export const useRouteGuards = (): useRouteGuardsReturnData => {
  const auth = useAuth();
  const prevRoute = useLocation();

  // Keep the current pathname in a ref so the guard components can read it fresh
  // for the post-login redirect without depending on it. Depending on the pathname
  // would give the memoized guards a new identity on every navigation, which remounts
  // the entire wrapped route subtree (and needlessly refetches page data such as KPIs).
  const pathnameRef = useRef(prevRoute.pathname);
  pathnameRef.current = prevRoute.pathname;

  const RequireAuth = useMemo(
    () =>
      ({ children }: { children: JSX.Element }): JSX.Element => {
        if (!auth.isAuthenticated()) {
          return <Navigate to="/logon" state={{ pathname: pathnameRef.current }} replace />;
        }
        return children;
      },
    [auth]
  );

  const RequireDeveloperAuth = useMemo(
    () =>
      ({ children }: { children: JSX.Element }): JSX.Element => {
        if (auth.currentUserType !== OrganisationTypeConstants.DEVELOPER) {
          return <Navigate to="/logon" replace />;
        }

        if (!auth.isAuthenticated()) {
          return <Navigate to="/logon" state={{ pathname: pathnameRef.current }} replace />;
        }
        return children;
      },
    [auth]
  );

  const RequireVerifierAuth = useMemo(
    () =>
      ({ children }: { children: JSX.Element }): JSX.Element => {
        if (auth.currentUserType !== OrganisationTypeConstants.VERIFIER) {
          return <Navigate to="/logon" replace />;
        }

        if (!auth.isAuthenticated()) {
          return <Navigate to="/logon" state={{ pathname: pathnameRef.current }} replace />;
        }

        return children;
      },
    [auth]
  );

  const RequireDevelopmentManagerAuth = useMemo(
    () =>
      ({ children }: { children: JSX.Element }): JSX.Element => {
        if (auth.currentUserType !== OrganisationTypeConstants.DEVELOPMENT_MANAGER) {
          return <Navigate to="/logon" replace />;
        }

        if (!auth.isAuthenticated()) {
          return <Navigate to="/logon" state={{ pathname: pathnameRef.current }} replace />;
        }

        return children;
      },

    [auth]
  );

  const RequireAssetManagerAuth = useMemo(
    () =>
      ({ children }: { children: JSX.Element }): JSX.Element => {
        if (auth.currentUserType !== OrganisationTypeConstants.ASSET_MANAGER) {
          return <Navigate to="/logon" replace />;
        }

        if (!auth.isAuthenticated()) {
          return <Navigate to="/logon" state={{ pathname: pathnameRef.current }} replace />;
        }
        return children;
      },
    [auth]
  );

  const RequireUserPermission = useMemo(
    () =>
      ({
        children,
        userPermission,
        redirectRoute,
      }: {
        children: JSX.Element;
        userPermission: string;
        redirectRoute?: string;
      }): JSX.Element => {
        if (!auth.hasPermission(userPermission)) {
          return <Navigate to={redirectRoute ?? getAccessDeniedRoute(auth.currentUserType)} replace />;
        }

        return children;
      },
    [auth]
  );

  // NOTE: this guard reads ProjectContext at its render position (inside ProjectProvider),
  // so the useContext call must stay inside the component rather than being hoisted. We only
  // memoize the component identity so navigation doesn't needlessly remount the wrapped route.
  const RequireProjectPermission = useMemo(() => {
    const RequireProjectPermissionGuard = ({
      children,
      redirectRoute,
    }: {
      children: JSX.Element;
      redirectRoute?: string;
    }): JSX.Element => {
      const { isExternalProject } = useContext(ProjectContext);

      if (isExternalProject()) {
        // Developer-only guard, so the default developer access-denied route is the right fallback.
        return <Navigate to={redirectRoute ?? getAccessDeniedRoute()} replace />;
      }

      return children;
    };
    return RequireProjectPermissionGuard;
  }, []);

  return {
    RequireAuth,
    RequireDeveloperAuth,
    RequireVerifierAuth,
    RequireDevelopmentManagerAuth,
    RequireAssetManagerAuth,
    RequireUserPermission,
    RequireProjectPermission,
  };
};
